<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue'
import Swiper from 'swiper'
import { Autoplay, Pagination } from 'swiper/modules'
import { chapterConfig as rocnovelConfig } from '../configs/rocnovel'
import { chapterConfig as ocereadConfig } from '../configs/oceread'
import { chapterConfig as ficmoonConfig } from '../configs/ficmoon'
import { useUserStore } from '@/stores'
import 'swiper/css'
import 'swiper/css/pagination'

import { usePurchase } from '@/composables/usePurchase'
import { useSubscript } from '@/composables/useSubscript'
import { SubscriptionCycle } from '@/utils/enum'
import { VIP_TYPE } from '@/constants'
import { brand, colors } from '@/config/brands'
import { loadBrandConfig } from '@/utils/loadBrandConfig'

const props = defineProps<{
  value: boolean
  contentId: string
  contentChapterId: string
  bookName: string
  chapterName: string
  serialStatus: number
}>()

const emit = defineEmits<{
  (event: 'input', value: boolean): void
  (event: 'success'): void
}>()

// 加载当前品牌的 chapter 配置
const config = loadBrandConfig({
  rocnovel: rocnovelConfig,
  oceread: ocereadConfig,
  ficmoon: ficmoonConfig,
})

const userStore = useUserStore()
const { templateProducts, PayTemplateSuccess, PayTemplate } = usePurchase('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
  bookName: props.bookName,
  chapterName: props.chapterName,
  serialStatus: props.serialStatus,
})
const { loaded, subscribeProducts, subscribeSuccess, getUnitPrice, onSubscribeProducts, getIsImageBackground, isZero } = useSubscript('chapter', {
  contentId: props.contentId,
  contentChapterId: props.contentChapterId,
})
const active = ref(0)

const subscribes = computed(() => {
  return subscribeProducts.value.filter(item => item.purchasePopShow)
})

function handlePayTemplate(item: any, index: number) {
  active.value = index
  PayTemplate(item)
}

function initSwiper() {
  nextTick(() => {
  // eslint-disable-next-line no-new
    new Swiper('.banner.swiper', {
      modules: [Pagination, Autoplay],
      spaceBetween: 0,
      autoplay: {
        delay: 3500,
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      loop: true,
    })
  })
}

watch(
  () => loaded.value,
  (val) => {
    if (val)
      initSwiper()
  },
)

watch(
  () => subscribeSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

watch(
  () => PayTemplateSuccess.value,
  (val) => {
    if (val)
      emit('success')
  },
)

// 定义样式变量供 CSS v-bind 使用
const vipBg = config.colors.vipBg
const purpleTextGradient = config.colors.purpleTextGradient
const pinkGradient = config.colors.pinkGradient
</script>

<template>
  <BasicPopup :value="value" @input="value => $emit('input', value)">
    <div class="popup-recharge w-full px-5 pt-20px pb-30px bg-n-bg-card rounded-t-lg relative">
      <h3 class="text-18px text-center text-n-text-1">
        {{ config.texts.payTemplateTitle }}
      </h3>
      <p class="text-14px text-n-text-2 text-center">
        {{ config.texts.payTemplateDesc }}
      </p>
      <!-- 支付 -->
      <div class="flex flex-wrap justify-between">
        <div
          v-for="(item, index) in templateProducts"
          :key="index"
          class="relative border b-rd-8px w-48% h-70px mt-4 flex flex-col justify-center items-center text-n-text-2"
          :style="{
            border: active === index ? `1px solid ${colors?.primary}` : `1px solid ${colors?.border.primary}`,
            background: active === index ? config.colors.vipBg : colors?.bg.card,
          }"
          @click="handlePayTemplate(item, index)"
        >
          <div
            v-if="item.marketingText"
            class="pos-absolute !text-white left--1px top--8px min-w-50px h-16px text-10px py-2px px-2 flex items-center"
            :style="{ background: config.colors.pinkGradient, borderRadius: '100px 100px 100px 0px' }"
          >
            {{ item.marketingText }}
          </div>
          <div>
            <span class="text-18px font-medium text-n-text-1">{{ item.coinsBuy }}+{{ item.coinsBonus }}</span> {{ config.texts.payTemplateCoins }}
          </div>
          <div class="mt-2px">
            $ {{ item.price }}
          </div>
        </div>
      </div>

      <!-- 订阅 -->
      <div v-if="!userStore.$state.subscribe && subscribes?.length" class="banner swiper mt-4">
        <div class="swiper-wrapper ">
          <div
            v-for="(item, index) in subscribes"
            :key="index"
            class="swiper-slide swiper-item rounded-8px"
            :style="{
              background: getIsImageBackground(item)
                ? `url(${config.colors.memberSubscribeBg}) no-repeat`
                : config.colors.subscribeActiveBg,
              backgroundSize: '100% 100%',
            }"
          >
            <div class="relative w-full h-full px-12px pt-20px pb-20px text-n-text-1" @click="onSubscribeProducts(item)">
              <!-- 角标 -->
              <div v-if="item.cornerTip && item.marketingCopy" class="popular-tag">
                {{ item.marketingCopy }}
              </div>

              <!-- 折扣会员 -->
              <div v-if="item.subscribeType === VIP_TYPE.DISCOUNT" class="flex items-center">
                <!-- 折扣会员的周期背景,例如: Weekly -->
                <div class="cycleBg absolute font-bold top-0 right-0 text-32px leading-[30px] text-n-primary">
                  {{ item.cycleStr }}
                </div>

                <img v-if="brand !== 'ficmoon'" src="@/assets/vip_discount.png" class="w-26px! flex-shrink-0">
                <div class="flex-1 ml-16px">
                  <div class="text-18px font-medium">
                    {{ item.discountCoins }} Coins
                  </div>
                  <ul class="text-n-primary text-12px list-disc pl-12px">
                    <li>{{ config.texts.payTemplateGetCoins.replace('{coins}', String(item.discountOnceCoins)) }}</li>
                    <li>{{ config.texts.payTemplateEarnCoins.replace('{coins}', String(item.discountBonusCoins)).replace('{daily}', String(item.discountDailyCoins)) }}</li>
                  </ul>
                </div>
                <div class="flex-shrink-0 text-right relative">
                  <div class="text-18px font-bold">
                    $ {{ item.preferentialPrice || item.price }}
                  </div>
                  <div class="text-n-text-2 text-12px line-through">
                    $ {{ item.discountOriginalPrice }}
                  </div>
                </div>
              </div>

              <!-- 畅读会员 -->
              <div v-else class="flex items-center relative h-full">
                <div v-if="brand !== 'ficmoon'" class="flex-shrink-0">
                  <img src="@/assets/vip_icon2.png" class="w-26px!">
                </div>

                <div class="flex-1 ml-8px flex flex-col justify-start">
                  <div class="text-18px font-medium">
                    {{ SubscriptionCycle.getItemByValue(item.cycle)?.label }}
                  </div>
                  <div class="text-n-primary truncate-2-lines">
                    {{ item.subscribeText }}
                  </div>
                </div>

                <!-- 免费 -->
                <div v-if="isZero(item.preferentialPrice)">
                  <div class="text-18px font-bold">
                    {{ config.texts.payTemplateFree }}
                  </div>
                  <div class="text-n-text-2 text-12px line-through text-right">
                    $ {{ item.price }}
                  </div>
                </div>

                <!-- 无优惠价格 -->
                <div v-else-if="!item.preferentialPrice" class="flex-shrink-0 text-right relative">
                  <div class="text-18px font-bold">
                    $ {{ item.price }}
                  </div>

                  <div v-if="getUnitPrice(item)" class="text-n-text-2 text-12px">
                    $ {{ getUnitPrice(item) }}
                  </div>
                </div>

                <!-- 有优惠价格 -->
                <div v-else>
                  <div class="text-18px font-bold">
                    $ {{ item.preferentialPrice }}
                  </div>
                  <div class="text-n-text-2 text-12px line-through text-right">
                    $ {{ item.price }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="swiper-pagination flex items-center justify-center" />
      </div>

      <div v-if="!loaded" class="h-260px flex items-center justify-center flex-col">
        <BasicSpinner class="mx-auto" />
        <div class="mt-2 font-bold">
          {{ config.texts.loading }}
        </div>
      </div>
    </div>
  </BasicPopup>
</template>

<style lang="scss" scoped>
.pay-template-info:hover {
  border: 1px solid var(--primary-color);
  background-color: v-bind(vipBg);
}
.swiper-item{
  height: 115px;
}
@media screen and (min-width: 380px) {
  .swiper-item{
    min-height: 91px;
  }
}
.cycleBg {
  background: v-bind(purpleTextGradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}
</style>

<style lang="scss">
.popup-recharge {
  .swiper-pagination-bullet {
    width: 6px;
    height: 6px;
    background: #ffffff;
    border-radius: 3px;
    opacity: 0.5;
  }

  .swiper-pagination-bullet-active {
    width: 20px;
    height: 6px;
    background: #ffffff;
    border-radius: 3px;
    opacity: 1;
  }

  .popular-tag {
    position: absolute;
    top: 0;
    left: 0;
    min-width: 56px;
    height: 18px;
    background: v-bind(pinkGradient);
    border-radius: 8px 0px 12px 0px;
    font-weight: 400;
    font-size: 10px;
    color: white !important;
    text-align: center;
  }

  .truncate-2-lines {
    width: 100%;
    word-break: break-all;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2; /* 这里是超出几行省略 */
    line-clamp: 2; /* Standard property for compatibility */
    overflow: hidden;
  }
}
</style>
